/* eslint-disable max-lines */
import {makeAutoObservable, observable, runInAction} from 'mobx'
import React from 'react'
import ScrollBar from 'react-perfect-scrollbar'
import {Lang} from 'yougile-lang'
import {Util, UUID} from 'yougile-util'
import {
  ColumnColor,
  DataObjectId,
  DeadlineStickerEntry,
  getColumnBgColorHex,
  getColumnColorHex,
  getColumnColorNumber,
  Hub,
  Tag,
  TaskData,
  TimetrackerBoardSettings,
  User
} from 'yougile-model'
import {Items} from '@api/model/items'
import {PermissionsChecker} from '@client/permissions-checker'
import {Sync} from '@client/sync'
import {DragData} from '@common/drag-data'
import {Tracking, TrackingEvent} from '@common/tracking'
import {BoardStore} from '@components/board/board-store'
import {
  showConfirm,
  showDeleteConfirm
} from '@components/common/base/notifications'
import {MessageParts} from '@components/common/base/notifications/show-confirm'
import {TextAreaApi} from '@components/common/base/textarea/types'
import {DraggableProps} from '@components/draggable'
import {MessageBox} from '@components/message-box'
import {multiActionsWithTasksStore} from '@components/multi-actions-with-tasks/multi-actions-with-tasks-store'
import {createUseStore} from '@containers/hooks'
import {Board} from '@extensions/board'
import {Column} from '@extensions/column'
import {globals} from '@extensions/globals-base'
import {PageService} from '@extensions/page-service'
import {BoardFilters} from '@extensions/sticker/board-filters'
import {
  Sticker,
  stickerCtl,
  systemStickerCtl
} from '@extensions/sticker/sticker'
import {Task} from '@extensions/task'
import {columnsSortPopupCtl} from './columns-sort/columns-sort-popup'
import {OrderSubMenuType, SetNewOrderProps} from './columns-sort/types'

const taskLimitInitial = 20
const taskLimitStep = 50

interface State {
  loading: boolean

  openTaskList: Map<DataObjectId, Hub[]>
  openTaskListSorted: Map<DataObjectId, Hub[]>
  closedTaskList: Map<DataObjectId, Hub[]>
  closedTaskListSorted: Map<DataObjectId, Hub[]>

  isMirror: boolean

  groupEdit: boolean
  groupEditDropdownIsShown: boolean

  taskCountLimits: {[columnId: string]: number}
  expandedCompleted: {[id: string]: boolean}

  moving: boolean

  orderSubMenu: {
    showed: boolean
    submenu?: OrderSubMenuType
  }
  stickerListener?: UUID
  color: ColumnColor

  numericStickersSum: Map<
    DataObjectId,
    {
      value: number
      title: string
    }
  >

  scrollableEl?: HTMLElement
  scrollBar?: ScrollBar

  visibleColumnId?: DataObjectId

  mirrorColumnPlaceholder?: {
    columnId: DataObjectId
    mirroredColumnId: DataObjectId
    height: number
  }

  visibleColumnTitle: string

  taskReportCount: number
  taskReportUsers: User[]
  taskReportUserInfo: Record<string, number>
  taskReportDeadlines: {
    deadline: DeadlineStickerEntry
    completed: boolean
  }[]
}

export class ColumnStore {
  state: State

  constructor(
    private boardStore: BoardStore,
    public column: Hub
  ) {
    this.state = {
      loading: true,
      openTaskList: new Map(),
      openTaskListSorted: new Map(),
      closedTaskList: new Map(),
      closedTaskListSorted: new Map(),

      moving: false,
      isMirror: Column.isMirror(column),
      groupEdit: false,
      groupEditDropdownIsShown: false,
      taskCountLimits: {},
      expandedCompleted: {},
      orderSubMenu: {showed: false},
      numericStickersSum: new Map(),

      color: getColumnColorNumber(this.column),
      visibleColumnTitle: this.column.title,

      taskReportCount: 0,
      taskReportUsers: [],
      taskReportUserInfo: {},
      taskReportDeadlines: []
    }

    makeAutoObservable<this, 'boardStore'>(
      this,
      {
        state: observable.shallow,
        column: false,
        boardStore: false
      },
      {autoBind: true}
    )

    this.syncOnUpdate()
  }

  syncOnNew(column: Hub): void {
    this.column = column
    this.syncOnUpdate()
  }

  syncOnUpdate(): void {
    this.state.color = getColumnColorNumber(this.column)
    this.state.isMirror = Column.isMirror(this.column)
    this.state.visibleColumnTitle = this.column.title
    this.updateTaskList()
    this.updateAggregatedStickerValues()
    setTimeout(() => {
      runInAction(() => (this.state.loading = false))
    })
    setTimeout(() => {
      try {
        this.state.scrollBar?.updateScroll()
      } catch (e) {
        // logger.warn(e)
      }
    }, 100)
  }

  initialize(): () => void {
    const listener = BoardFilters.addListener(() => {
      this.updateTaskList()
      this.updateAggregatedStickerValues()
      setTimeout(() => {
        this.state.scrollBar?.updateScroll()
      }, 100)
    })
    return () => {
      BoardFilters.removeListener(listener)
    }
  }

  getTimeTrackingCong(): TimetrackerBoardSettings | undefined {
    const box = Sync.getCurrentSandbox()
    const sticker = stickerCtl.Timetracking.getSticker(box)
    const board = box.get(this.column.location) as Hub
    if (!board || !(sticker.id in board.stickers)) {
      return undefined
    }
    return board.stickers[sticker.id] as TimetrackerBoardSettings
  }

  hasTimetracking(): boolean {
    const conf = this.getTimeTrackingCong()

    if (!conf) {
      return false
    }

    return !conf.exclude || !(this.column.id in conf.exclude)
  }

  groupStartEdit(): void {
    if (!PermissionsChecker.checkRename(this.column)) {
      return
    }
    this.state.groupEdit = true
  }

  groupStartEditOrShowWarn(): void {
    if (!PermissionsChecker.checkRename(this.column)) {
      MessageBox.warn(Lang.msg.permissionsForbiddenRename)
      return
    }
    this.state.groupEdit = true
    this.state.groupEditDropdownIsShown = false
  }

  groupEditOpenMenu(): void {
    if (!multiActionsWithTasksStore.state.visible) {
      this.state.groupEditDropdownIsShown = true
    }
  }

  groupEditCloseMenu(): boolean {
    this.state.groupEditDropdownIsShown = false
    this.hideOrderSubMenu()
    return true
  }

  groupEditSubmit(edit: TextAreaApi): void {
    this.state.groupEditDropdownIsShown = false
    edit.text = (edit.text || '').trim()
    if (edit.text && edit.text !== this.column.title) {
      if (!Items.rename(Items.fromHub(this.column), edit.text)) {
        this.state.groupEdit = false
        return
      }
      const box = Sync.getCurrentSandbox()
      box.editTitle(this.column, edit.text)
      this.state.visibleColumnTitle = edit.text
    }
    this.state.groupEdit = false
  }

  groupEditCancel(): void {
    this.state.groupEdit = false
  }

  setNewOrder(props: SetNewOrderProps): void {
    columnsSortPopupCtl.setNewOrder(props)
    this.groupEditCloseMenu()
  }

  getTaskCountLimit(column: Hub): number {
    return this.state.taskCountLimits[column.id] || taskLimitInitial
  }

  taskCountShowMore(column: Hub): void {
    this.state.taskCountLimits = {
      ...this.state.taskCountLimits,
      [column.id]:
        (this.state.taskCountLimits[column.id] || taskLimitInitial) +
        taskLimitStep
    }

    this.recalcSorted(column)
  }

  toggleExpandedCompleted(id: DataObjectId): void {
    this.state.expandedCompleted = {
      ...this.state.expandedCompleted,
      [id]: !this.state.expandedCompleted[id]
    }
  }

  selectMultipleTasksId(id: DataObjectId, e: React.MouseEvent): void {
    if ((e.metaKey || e.ctrlKey) && !multiActionsWithTasksStore.state.visible) {
      multiActionsWithTasksStore.visibleOn()
      void Tracking.send(TrackingEvent.multiActionsWithTasksSelectWithHotKey)
    }
    multiActionsWithTasksStore.state.visible &&
      multiActionsWithTasksStore.setTasksId(id)
  }

  taskArchivedOrder(task: Hub): number {
    return -(task.complete || task.timestamp)
  }

  async archiveTasks(completed: boolean): Promise<void> {
    this.state.groupEditDropdownIsShown = false
    if (
      !(await showConfirm(
        completed
          ? Lang.msg.sureAchiveCompletedTasks
          : Lang.msg.sureAchiveAllTasks
      ))
    ) {
      return
    }
    void Tracking.send(
      completed
        ? TrackingEvent.taskArchiveAllCompleted
        : TrackingEvent.taskArchiveAllInColumn
    )
    const tasks = completed
      ? Column.listTasks(this.column).filter(
          t => !Task.isArchived(t) && Task.getGTDStage(t) === 'done'
        )
      : BoardFilters.filter(
          PageService.getCurrentBoard(),
          Column.listTasks(this.column)
        )
    let forbidden = false
    for (const task of tasks) {
      if (!PermissionsChecker.checkArchive(task)) {
        forbidden = true
      } else {
        Task.forceChangeCompleteness(task, true)
      }
    }
    if (forbidden) {
      MessageBox.warn(Lang.msg.permissionsForbiddenTaskClose)
    }
  }

  onMoveTargetChoose(boards: Hub[]): void {
    if (!boards || boards.length !== 1) {
      this.state.moving = false
      this.state.groupEditDropdownIsShown = false
      return
    }
    const to = boards[0]
    const box = Sync.getCurrentSandbox()
    const from = box.get(this.column.location) as Hub
    if (from && to.id !== from.id) {
      if (!PermissionsChecker.checkMove(this.column, from, to)) {
        MessageBox.warn(Lang.msg.permissionsForbiddenMove)
      } else {
        Board.columnDropHandler(0, this.column, from, to)
        void Sticker.moveTaskHook(this.column, from, to, to.id).then(
          allowed =>
            !allowed && Board.columnDropHandler(0, this.column, to, from) // reverting
        )
      }
    }
    this.state.moving = false
    this.state.groupEditDropdownIsShown = false
  }

  moveOpen(): void {
    this.state.moving = true
  }

  moveClose(): void {
    this.state.moving = false
  }

  duplicate(): void {
    setTimeout(() => {
      this.state.groupEditDropdownIsShown = false
    }, 100)
    void Column.duplicate(this.column)
  }

  async delete(): Promise<void> {
    this.state.groupEditDropdownIsShown = false
    let texts: MessageParts = {
      title: Lang.msg.deleteGroupWarn,
      subtitle: Lang.msg.deleteGroupWarnHint
    }
    if (Column.isMirror(this.column)) {
      texts = {
        title: Lang.msg.deleteMirrorWarn,
        subtitle: Lang.msg.deleteMirrorWarnHint
      }
    }
    if (!PermissionsChecker.checkDeletion(this.column)) {
      MessageBox.warn(Lang.msg.permissionsForbiddenColumnDelete)
    } else if (await showDeleteConfirm(texts)) {
      if (!Items.delete(Items.fromHub(this.column))) {
        this.boardStore.syncOnUpdate()
        return
      }
      Board.removeColumn(this.column)
      this.boardStore.syncOnUpdate()
      Items.afterDelete(Items.fromHub(this.column))
    }
  }

  columnSortAllowed(): boolean {
    const tasks = Column.listTasks(this.column, false)
    if (!tasks?.length) {
      return false
    }

    const [task] = tasks
    return globals.dragAllowed(task.id)
  }

  showOrderSubMenu(): void {
    this.state.orderSubMenu = {...this.state.orderSubMenu, showed: true}
  }

  showOrderCustomSubMenu(type: OrderSubMenuType): void {
    this.state.orderSubMenu = {
      ...this.state.orderSubMenu,
      showed: true,
      submenu: type
    }
  }

  hideOrderSubMenu(): void {
    this.state.orderSubMenu = {
      ...this.state.orderSubMenu,
      showed: false,
      submenu: undefined
    }
  }

  getCustomStickers(): Tag[] {
    const box = Sync.getCurrentSandbox()
    const board = box.getHub(this.column.location)
    const stickerTags = Sticker.list(board)

    if (!board?.stickers) {
      return []
    }

    const systemStickersIds = Object.values(systemStickerCtl).map(st => {
      return st.getSticker(box)?.id
    })

    const customStickers: string[] = []
    Object.keys(board.stickers).map((stickerId: string) => {
      if (!systemStickersIds.find(el => el === stickerId)) {
        customStickers.push(stickerId)
      }
    })

    return stickerTags.filter(s => customStickers.includes(s.id))
  }

  async onDrop(event: React.DragEvent<HTMLDivElement>): Promise<void> {
    const box = Sync.getCurrentSandbox()
    const data = DragData.get()
    if (data?.type === 'user' || data?.type === 'sticker') {
      event.preventDefault()
      const sticker =
        data.type === 'user'
          ? systemStickerCtl.User.getSticker(box)
          : (box.get(data.stickerId) as Tag)
      const state = data.type === 'user' ? data.userId : data.value
      const group = this.column
      const tasks = BoardFilters.filter(
        PageService.getCurrentBoard(),
        Column.listTasks(group)
      ).filter(t => !!t && !t.complete)

      DragData.setCurrent({type: 'empty'})

      const incorrect =
        state &&
        tasks.find(
          t => !PermissionsChecker.checkEditTaskStickers(t, sticker, state)
        )
      if (incorrect) {
        MessageBox.warn(Lang.msg.permissionsForbiddenTaskStickers)
        return
      }
      const type = Sticker.getType(sticker)
      if (type === 'user' || Sticker.isCustom(sticker)) {
        if (await showConfirm(Lang.msg.pinStickerToColumn)) {
          tasks.forEach((task: Hub) => {
            Sticker.pin(sticker, group.id, task, state)
          })
          void Tracking.send(TrackingEvent.stickerDropOnColumn)
        }
      } else {
        MessageBox.warn(Lang.msg.pinStickerToColumnUnavailable)
      }
    }
  }

  onDragOver(event: React.DragEvent<HTMLDivElement>): void {
    const type = DragData.get()?.type
    if (type === 'user' || type === 'sticker') {
      event.preventDefault()
      DragData.setEffect(event, 'copy')
    }
  }

  onDragLeave(event: React.DragEvent<HTMLDivElement>): void {
    const type = DragData.get()?.type
    if (type === 'user' || type === 'sticker') {
      event.preventDefault()
      DragData.setEffect(event, 'none')
    }
  }
  getOpenTasks(column: Hub | undefined): Hub[] {
    if (!column) {
      return []
    }
    return this.state.openTaskList.get(column.id) || []
  }
  getOpenSortedTasks(column: Hub | undefined): Hub[] {
    if (!column) {
      return []
    }
    return this.state.openTaskListSorted.get(column.id) || []
  }
  getClosedTasks(column: Hub | undefined): Hub[] {
    if (!column) {
      return []
    }
    return this.state.closedTaskList.get(column.id) || []
  }
  getClosedSortedTasks(column: Hub | undefined): Hub[] {
    if (!column) {
      return []
    }
    return this.state.closedTaskListSorted.get(column.id) || []
  }

  /**
   * Update source column just after dragging before render so no tasks blink
   */
  updateSourceColumnOnDrop(
    draggableProps: DraggableProps | undefined,
    column: Hub
  ): void {
    const data = draggableProps?.data
    const taskDrag = data?.type === 'task'
    if (
      !taskDrag ||
      data.payload.columnId !== column.id ||
      Column.isMirror(column)
    ) {
      return
    }
    this.updateTaskList(column)
  }

  updateTaskList(column?: Hub, triggerListUpdate = true): void {
    column = column ?? this.column

    if (Column.isMirror(column)) {
      const columns = Column.getMirroredColumns(column)
      columns.forEach(g => {
        this.updateTaskList(g, false)
      })
      this.triggerListsUpdate()
    } else {
      const res = this.getTasks(column)
      const newOpenList = []
      const newClosedList = []

      for (const t of res) {
        if (Task.isArchived(t)) {
          newClosedList.push(t)
        } else {
          newOpenList.push(t)
        }
      }

      const changedOpenedSize =
        newOpenList.length !== this.state.openTaskList.get(column.id)?.length

      this.state.openTaskList.set(column.id, newOpenList)
      this.state.closedTaskList.set(column.id, newClosedList)
      this.recalcSorted(column)

      if (triggerListUpdate) {
        this.triggerListsUpdate()
      }

      if (changedOpenedSize) {
        setTimeout(() => {
          // Необходимо после добавления задачи при клике на эл-ты другой задачи
          // обновляет положение попапов (стикеры, меню задачи)
          window.dispatchEvent(new Event('resize'))
        }, 0)
      }
    }
  }

  recalcSorted(column: Hub): void {
    column = column ?? this.column

    if (!column) {
      this.state.openTaskListSorted = new Map()
      this.state.closedTaskListSorted = new Map()
      return
    }

    Column.invalidateTasksOrderCache(column.id)

    if (Column.isMirror(column)) {
      const mirroredColumns = Column.getMirroredColumns(column)
      for (const mirroredColumn of mirroredColumns) {
        this.recalcSorted(mirroredColumn)
      }
      return
    }

    const limit = this.getTaskCountLimit(column)
    const orderInstance = Column.getTasksOrder(column)

    const openO = orderInstance.order
    const openOrderFunc = (a: Hub, b: Hub): number => openO(a) - openO(b)
    const openTaskList = this.state.openTaskList.get(column.id)

    if (openTaskList) {
      const sorted = [...openTaskList].sort(openOrderFunc)
      this.state.openTaskListSorted.set(column.id, sorted.slice(0, limit))
    } else {
      this.state.openTaskListSorted.set(column.id, [])
    }

    const closedO = this.taskArchivedOrder
    const closedOrderFunc = (a: Hub, b: Hub): number => closedO(a) - closedO(b)
    const closedTaskList = this.state.closedTaskList.get(column.id)

    if (closedTaskList) {
      const sortedC = [...closedTaskList].sort(closedOrderFunc)
      this.state.closedTaskListSorted.set(column.id, sortedC.slice(0, limit))
    } else {
      this.state.closedTaskListSorted.set(column.id, [])
    }
  }

  private triggerListsUpdate(): void {
    this.state.openTaskList = new Map(this.state.openTaskList)
    this.state.closedTaskList = new Map(this.state.closedTaskList)
    this.state.openTaskListSorted = new Map(this.state.openTaskListSorted)
    this.state.closedTaskListSorted = new Map(this.state.closedTaskListSorted)
  }

  updateAggregatedStickerValues(tasks?: (Hub | undefined)[]): void {
    if (this.state.isMirror && !this.column.data.report) {
      return
    }

    const box = Sync.getCurrentSandbox()
    const board = box.getHub(this.column.location)

    if (!board?.data?.stickersSettings) {
      return
    }
    this.state.numericStickersSum = new Map()

    const stickersSettings = board.data.stickersSettings
    const stickersSettingsKeys = Object.keys(stickersSettings) as DataObjectId[]
    const aggregatedStickersId = stickersSettingsKeys.filter(stickerId =>
      Boolean(stickersSettings[stickerId]?.showNumericStickersSum)
    )

    if (aggregatedStickersId.length === 0) {
      return
    }

    const numericStickersSum = new Map()
    const currentTasks =
      tasks ?? this.state.openTaskList.get(this.column.id) ?? []
    const idCountedTasks: Set<DataObjectId> = new Set()

    currentTasks.forEach((task: Hub) => {
      if (idCountedTasks.has(task.id)) {
        return
      }
      idCountedTasks.add(task.id)

      const taskStickers = Sticker.getAllPinned(task) || []
      taskStickers.forEach((sticker: Tag) => {
        if (!aggregatedStickersId.includes(sticker.id)) {
          return
        }

        const prevValue = numericStickersSum.get(sticker.id)
        const stickerValue = Number(task.stickers[sticker.id]) || 0
        const value = ((prevValue?.value || 0) + stickerValue).toFixed(4)

        const numericSumStickerValue = {
          value: Number(value),
          title: sticker.title || prevValue?.title || ''
        }

        numericStickersSum.set(sticker.id, numericSumStickerValue)
      })

      this.state.numericStickersSum = numericStickersSum
    })
  }

  public getTasks(column: Hub): Hub[] {
    const res = Items.list(
      Items.fromHub(column),
      Column.listTasks(column).map(Items.fromHub)
    ).map(Items.toHub)
    return Util.removeDuplicateIds(res)
  }

  onSubtasksColumnDragOver(e: React.MouseEvent, columnId?: DataObjectId): void {
    const data = DragData.get()
    if (data?.type === 'task') {
      e.stopPropagation()
      e.preventDefault()

      this.toggleSubtaskPlaceholderForColumn(columnId, true)
    }
  }

  onSubtasksColumnDragLeave(columnId?: DataObjectId): void {
    const data = DragData.get()
    if (data?.type === 'task') {
      this.toggleSubtaskPlaceholderForColumn(columnId, false)
    }
  }

  private toggleSubtaskPlaceholderForColumn(
    columnId: DataObjectId | undefined,
    flag: boolean
  ): void {
    const scrollableEl = this.state.scrollableEl
    const columnEl = scrollableEl?.querySelector(
      `[data-column-id="${columnId}"] .proj-task-container`
    )
    const placeholderEl = columnEl?.querySelector('.subtask-placeholder')
    if (flag) {
      columnEl?.classList.add('min-h-8')
      placeholderEl?.classList.remove('hidden')
    } else {
      columnEl?.classList.remove('min-h-8')
      placeholderEl?.classList.add('hidden')
    }
  }

  onSubtaskColumnDrop(column: Hub): void {
    const report = !!column?.data?.report
    if (report) {
      return
    }

    const data = DragData.get()
    if (data?.type === 'task') {
      const order = Column.getTasksOrder(column)
      const index = order.getList().filter((x: Hub) => !x.complete).length
      const task = Task.getById(data.id)
      task && Column.taskDropHandler(index, task, column, {type: 'drag'})
      this.toggleSubtaskPlaceholderForColumn(column.id, false)
    }
  }

  setScrollableEl(el: HTMLElement): void {
    this.state.scrollableEl = el
  }

  setScrollBar(instance: ScrollBar): void {
    this.state.scrollBar = instance
  }

  setVisibleColumnId(id: DataObjectId): void {
    this.state.visibleColumnId = id
  }

  getVisibleColumnId(): DataObjectId | undefined {
    let columnId = this.state.visibleColumnId
    if (!columnId && !this.state.loading) {
      const allColumns = Column.getMirroredColumns(this.column)
      columnId = allColumns[0]?.id
    }
    return columnId
  }

  // A mirror column title is also a dropzone for the scrolled to mirrored column
  setCustomPlaceholderForMirrorColumn(
    columnId: DataObjectId,
    mirroredColumnId: DataObjectId,
    height: number
  ): void {
    this.state.mirrorColumnPlaceholder = {columnId, mirroredColumnId, height}
  }

  clearCustomPlaceholderForMirrorColumn(): void {
    this.state.mirrorColumnPlaceholder = undefined
  }

  updateReportData(tasks: TaskData[]): void {
    const box = Sync.getCurrentSandbox()
    const users: Record<string, number> = {}
    const deadlines: {
      deadline: DeadlineStickerEntry
      completed: boolean
    }[] = []

    tasks.forEach(t => {
      if (t.assigned) {
        t.assigned.forEach(uid => (users[uid] = (users[uid] ?? 0) + 1))
      }
      const taskHub = box.getHub(t.id)
      if (taskHub) {
        const dl = Sticker.getDeadline(taskHub)
        const deadline = dl?.current
        if (deadline) {
          deadlines.push({
            deadline,
            completed: t.gtdStage === 'done'
          })
        }
      }
    })

    const userList = Object.keys(users)
      .map(uid => box.getUser(uid as DataObjectId))
      .filter((x): x is User => !!x)

    this.state.taskReportCount = tasks.length
    this.state.taskReportUsers = userList
    this.state.taskReportUserInfo = users
    this.state.taskReportDeadlines = deadlines
  }

  get columnStyle(): React.CSSProperties {
    // report columns are styled using classes
    if (this.column.data.report || this.state.isMirror || !this.state.color) {
      return {}
    }

    // basic&mirror column bg&border style depends on column color&column bg user prefs
    const color = this.state.color
    const borderColor = getColumnColorHex(color, globals.theme)
    const backgroundColor = globals.columnColorEnabled
      ? getColumnBgColorHex(color, globals.theme)
      : undefined
    return {borderColor, backgroundColor}
  }

  get isAutoTaskCover(): boolean {
    const {isAutoTaskCover, autoTaskCoverColumnIds} = this.boardStore.state
    return isAutoTaskCover && autoTaskCoverColumnIds.has(this.column.id)
  }
}

export const [ColumnContext, useColumnStore] = createUseStore(ColumnStore)

export const getGroupLocationPath = (group?: Hub): Hub[] => {
  const box = Sync.getCurrentSandbox()
  const board = group && box.getHub(group.location)
  const project = board && box.getHub(board.location)
  return project && board && group ? [project, board, group] : []
}
